import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import {
  FaUserPlus,
  FaMagnifyingGlass,
  FaStore,
  FaPhone,
  FaCalendarDays,
  FaPenToSquare,
  FaTrashCan,
  FaFilter,
  FaMoneyBillTrendUp,
  FaWallet,
} from "react-icons/fa6";
import { Header } from "../../../components/Header";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { useHookReseller } from "../../../hooks/useHookReseller";
import { ResellerCreate } from "./ResellerCreate";
import { ResellerService } from "../../../services/ResellerService";
import { PopUpDeleteConfirmation } from "../../../components/popups/PopUpDeleteConfirmation";
import { BaseUrl } from "../../../utilities/BaseUrl";
import { useNavigate } from "react-router-dom";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { formatDate } from "../../../utilities/formatDate";

/* ============================================
   UTILITY: Format Rupiah
   ============================================ */
const formatRupiah = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 8 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-700/60" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/60 rounded-md w-28" />
          <div className="h-3 bg-slate-700/40 rounded-md w-16" />
        </div>
      </div>
      <div className="h-6 bg-slate-700/40 rounded-full w-14" />
    </div>
    <div className="grid grid-cols-2 gap-3 mb-4">
      <div className="h-12 bg-slate-700/30 rounded-xl" />
      <div className="h-12 bg-slate-700/30 rounded-xl" />
    </div>
    <div
      className="flex justify-between items-center pt-3"
      style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
    >
      <div className="h-3 bg-slate-700/30 rounded w-24" />
      <div className="flex gap-2">
        <div className="w-8 h-8 bg-slate-700/40 rounded-lg" />
        <div className="w-8 h-8 bg-slate-700/40 rounded-lg" />
      </div>
    </div>
  </div>
);

/* ============================================
   KOMPONEN: Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "Aktif" || status === "aktif";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
        isActive
          ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
          : "bg-red-500/12 text-red-400 border border-red-500/20"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isActive ? "bg-emerald-400" : "bg-red-400"
        }`}
      />
      {status || "-"}
    </span>
  );
};

/* ============================================
   KOMPONEN: Amount Cell (Tabel)
   ============================================ */
const AmountCell = ({ amount, type = "income" }) => {
  const isIncome = type === "income";
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isIncome
            ? "bg-emerald-500/10 border border-emerald-500/15"
            : "bg-cyan-500/10 border border-cyan-500/15"
        }`}
      >
        {isIncome ? (
          <FaMoneyBillTrendUp className="text-[10px] text-emerald-400" />
        ) : (
          <FaWallet className="text-[10px] text-cyan-400" />
        )}
      </div>
      <span
        className={`text-sm font-semibold tabular-nums ${
          isIncome ? "text-emerald-400" : "text-cyan-400"
        }`}
      >
        {formatRupiah(amount)}
      </span>
    </div>
  );
};

/* ============================================
   KOMPONEN: Amount Card (Mobile)
   ============================================ */
const AmountCard = ({ label, amount, icon: Icon, color = "emerald" }) => {
  const colorMap = {
    emerald: {
      bg: "rgba(16, 185, 129, 0.06)",
      border: "rgba(16, 185, 129, 0.12)",
      icon: "rgba(16, 185, 129, 0.15)",
      text: "#34d399",
      label: "text-emerald-500/70",
    },
    cyan: {
      bg: "rgba(0, 180, 216, 0.06)",
      border: "rgba(0, 180, 216, 0.12)",
      icon: "rgba(0, 180, 216, 0.15)",
      text: "#48CAE4",
      label: "text-cyan-500/70",
    },
  };

  const c = colorMap[color] || colorMap.emerald;

  return (
    <div
      className="rounded-xl px-3 py-2.5"
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div
          className="w-5 h-5 rounded-md flex items-center justify-center"
          style={{ background: c.icon }}
        >
          <Icon className="text-[9px]" style={{ color: c.text }} />
        </div>
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${c.label}`}>
          {label}
        </span>
      </div>
      <p className="text-sm font-bold tabular-nums pl-7" style={{ color: c.text }}>
        {formatRupiah(amount)}
      </p>
    </div>
  );
};

/* ============================================
   KOMPONEN: Summary Bar
   ============================================ */
const SummaryBar = ({ data }) => {
  const summary = useMemo(() => {
    if (!data || data.length === 0) return { totalIncome: 0, totalStore: 0 };
    return data.reduce(
      (acc, item) => ({
        totalIncome: acc.totalIncome + (parseFloat(item.total_earnings) || 0),
        totalStore: acc.totalStore + (parseFloat(item.store_balance) || 0),
      }),
      { totalIncome: 0, totalStore: 0 }
    );
  }, [data]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div
        className="rounded-xl px-4 py-3 flex items-center gap-3"
        style={{
          background: "rgba(16, 185, 129, 0.04)",
          border: "1px solid rgba(16, 185, 129, 0.1)",
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(16, 185, 129, 0.12)" }}
        >
          <FaMoneyBillTrendUp className="text-emerald-400" />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500/60">
            Total Penghasilan
          </p>
          <p className="text-base font-bold text-emerald-400 tabular-nums">
            {formatRupiah(summary.totalIncome)}
          </p>
        </div>
      </div>

      <div
        className="rounded-xl px-4 py-3 flex items-center gap-3"
        style={{
          background: "rgba(0, 180, 216, 0.04)",
          border: "1px solid rgba(0, 180, 216, 0.1)",
        }}
      >
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(0, 180, 216, 0.12)" }}
        >
          <FaWallet className="text-cyan-400" />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-cyan-500/60">
            Total Saldo di Store
          </p>
          <p className="text-base font-bold text-cyan-400 tabular-nums">
            {formatRupiah(summary.totalStore)}
          </p>
        </div>
      </div>
    </div>
  );
};

/* ============================================
   KOMPONEN UTAMA: Reseller
   ============================================ */
export const Reseller = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  /* ---------- Filter & Search (client-side) ---------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const { data, loading, refreshData } = useHookReseller();

  /* ---------- Client-side Filter ---------- */
  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => {
      const matchesSearch =
        item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.phone_number && item.phone_number.includes(searchTerm));
      const itemStatus = item?.status?.name || item?.status;
      const matchesStatus = filterStatus === "" || itemStatus === filterStatus;

      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, filterStatus]);

  /* ---------- Extract unique filters ---------- */
  const statuses = useMemo(
    () => [
      ...new Set(
        data?.map((i) => i?.status?.name || i?.status).filter(Boolean)
      ),
    ],
    [data]
  );

  /* ---------- Delete State ---------- */
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [targetDeleteId, setTargetDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const handleEdit = (e, item) => {
    e.stopPropagation();
    setSelectedData(item);
    setOpenModalEdit(true);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setLoadingDelete(true);
    try {
      await ResellerService.delete({ id: targetDeleteId });
      refreshData();
      setOpenDeleteConfirmation(false);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingDelete(false);
    }
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "55px" },
    { key: "name", label: "Nama Reseller", align: "left" },
    { key: "contact", label: "Kontak", align: "left", width: "140px" },
    { key: "join_date", label: "Bergabung", align: "left", width: "140px" },
    { key: "earnings", label: "Penghasilan", align: "right", width: "165px" },
    { key: "store_balance", label: "Saldo Store", align: "right", width: "165px" },
    { key: "status", label: "Status", align: "center", width: "110px" },
    { key: "action", label: "Aksi", align: "center", width: "90px" },
  ];

  return (
    <>
      <Helmet>
        <title>Reseller Management - DANELA NET</title>
        <meta
          name="description"
          content="Kelola data reseller, pantau status, dan riwayat transaksi."
        />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Management Reseller
            </h2>
            <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              {filteredData.length} reseller
            </span>
          </div>
          <BtnDefault onClick={() => setOpenModalCreate(true)}>
            <span className="hidden lg:flex items-center gap-2">
              <FaUserPlus className="text-xs" />
              Tambah Data
            </span>
            <FaUserPlus className="lg:hidden" />
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Summary Bar ===== */}
          <SummaryBar data={data} />

          {/* ===== Search & Filter ===== */}
          <section
            className="rounded-2xl p-4 sm:p-5"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Filter dan pencarian"
          >
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Search */}
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label
                  htmlFor="search-reseller"
                  className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                >
                  Cari Data
                </label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input
                    id="search-reseller"
                    type="text"
                    placeholder="Nama atau telepon..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Filter
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  >
                    <option value="" className="bg-slate-800">
                      Semua Status
                    </option>
                    {statuses.map((s) => (
                      <option key={s} value={s} className="bg-slate-800">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel reseller"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center"
                            ? "text-center"
                            : col.align === "right"
                            ? "text-right"
                            : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {/* Loading */}
                  {loading && !data && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length} />
                      ))}
                    </>
                  )}

                  {/* Empty */}
                  {!loading && filteredData?.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-4 py-16 text-center"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaStore className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">
                              Tidak ada reseller ditemukan
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {searchTerm || filterStatus
                                ? "Coba ubah filter atau kata kunci pencarian"
                                : "Belum ada data reseller yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data Rows */}
                  {!loading &&
                    filteredData?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150 cursor-pointer"
                        style={{ background: "transparent" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0, 180, 216, 0.04)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                        onClick={() =>
                          navigate(`/dashboard/reseller/detail/${item.id}`)
                        }
                      >
                        {/* No */}
                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                          {index + 1}
                        </td>

                        {/* Name & Avatar */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {!item?.image ? (
                              <div
                                className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0"
                                style={{
                                  background:
                                    "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                                  border: "1px solid rgba(0, 180, 216, 0.15)",
                                  color: "#48CAE4",
                                }}
                              >
                                {item.name?.charAt(0).toUpperCase()}
                              </div>
                            ) : (
                              <img
                                src={BaseUrl + "/storage/" + item.image}
                                alt={item.name}
                                className="w-9 h-9 rounded-xl object-cover flex-shrink-0"
                                style={{
                                  border: "1px solid rgba(51, 65, 85, 0.3)",
                                }}
                              />
                            )}
                            <span className="font-semibold text-gray-100 truncate">
                              {item.name}
                            </span>
                          </div>
                        </td>

                        {/* Contact */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-400">
                            <FaPhone className="text-xs text-slate-500" />
                            <span className="text-sm italic">
                              {item.phone_number || "-"}
                            </span>
                          </div>
                        </td>

                        {/* Join Date */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-400">
                            <FaCalendarDays className="text-xs text-slate-500" />
                            <span className="text-sm">
                              {item.joining_date && formatDate(item.joining_date)}
                            </span>
                          </div>
                        </td>

                        {/* Penghasilan */}
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end">
                            <AmountCell
                              amount={item.total_earnings}
                              type="income"
                            />
                          </div>
                        </td>

                        {/* Saldo Store */}
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end">
                            <AmountCell
                              amount={item.store_balance}
                              type="store"
                            />
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 text-center">
                          <StatusBadge
                            status={item?.status?.name || item?.status}
                          />
                        </td>

                        {/* Action */}
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-1.5">
                            <button
                              onClick={(e) => handleEdit(e, item)}
                              className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 transition-all"
                              title="Edit reseller"
                            >
                              <FaPenToSquare className="text-sm" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTargetDeleteId(item.id);
                                setOpenDeleteConfirmation(true);
                              }}
                              className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-700/40 transition-all"
                              title="Hapus reseller"
                            >
                              <FaTrashCan className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Card List ===== */}
          <section className="lg:hidden space-y-3" aria-label="Daftar reseller">
            {/* Loading */}
            {loading && !data && (
              <>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </>
            )}

            {/* Empty */}
            {!loading && filteredData?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: "rgba(51, 65, 85, 0.2)",
                    border: "1px solid rgba(51, 65, 85, 0.2)",
                  }}
                >
                  <FaStore className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">
                  Tidak ada reseller ditemukan
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {searchTerm || filterStatus
                    ? "Coba ubah filter pencarian"
                    : "Belum ada data reseller"}
                </p>
              </div>
            )}

            {/* Cards */}
            {!loading &&
              filteredData?.map((item, index) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(0, 180, 216, 0.2)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background =
                      "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Header Card */}
                  <div
                    className="px-4 py-3 flex justify-between items-center"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
                    }}
                  >
                    <div className="flex items-center gap-2 text-slate-500 font-mono text-xs font-bold">
                      #{index + 1}
                    </div>
                    <StatusBadge
                      status={item?.status?.name || item?.status}
                    />
                  </div>

                  {/* Konten Card - Dibuat klikabel untuk navigasi */}
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() =>
                      navigate(`/dashboard/reseller/detail/${item.id}`)
                    }
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {!item?.image ? (
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0"
                            style={{
                              background:
                                "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                              border: "1px solid rgba(0, 180, 216, 0.15)",
                              color: "#48CAE4",
                            }}
                          >
                            {item.name?.charAt(0).toUpperCase()}
                          </div>
                        ) : (
                          <img
                            src={BaseUrl + "/storage/" + item.image}
                            alt={item.name}
                            className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                            style={{
                              border: "1px solid rgba(51, 65, 85, 0.3)",
                            }}
                          />
                        )}
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-100 leading-tight text-sm truncate">
                            {item.name}
                          </h3>
                          <div className="flex items-center gap-1.5 text-slate-500 mt-1">
                            <FaPhone className="text-[10px]" />
                            <span className="text-xs font-medium">
                              {item.phone_number || "-"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Amount Cards */}
                    <div className="grid grid-cols-2 gap-2.5 mb-3">
                      <AmountCard
                        label="Penghasilan"
                        amount={item.total_earnings}
                        icon={FaMoneyBillTrendUp}
                        color="emerald"
                      />
                      <AmountCard
                        label="Saldo Store"
                        amount={item.store_balance}
                        icon={FaWallet}
                        color="cyan"
                      />
                    </div>

                    <div
                      className="flex items-center justify-between pt-3"
                      style={{
                        borderTop: "1px solid rgba(51, 65, 85, 0.15)",
                      }}
                    >
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <FaCalendarDays className="text-[10px]" />
                        <span className="text-xs font-medium">
                          {item.joining_date
                            ? formatDate(item.joining_date)
                            : "Tidak ada tanggal"}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          onClick={(e) => handleEdit(e, item)}
                          className="p-2.5 rounded-xl text-slate-400 hover:text-cyan-400 transition-all"
                          style={{
                            background: "rgba(0, 180, 216, 0.06)",
                            border: "1px solid rgba(0, 180, 216, 0.1)",
                          }}
                        >
                          <FaPenToSquare className="text-sm" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTargetDeleteId(item.id);
                            setOpenDeleteConfirmation(true);
                          }}
                          className="p-2.5 rounded-xl text-slate-400 hover:text-red-400 transition-all"
                          style={{
                            background: "rgba(239, 68, 68, 0.06)",
                            border: "1px solid rgba(239, 68, 68, 0.1)",
                          }}
                        >
                          <FaTrashCan className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
          </section>
        </main>
      </DashboardLayout>

      {/* ===== Modals & Popups ===== */}
      <ResellerCreate
        open={openModalCreate}
        setOpen={setOpenModalCreate}
        refreshData={refreshData}
      />

      {/* Pastikan Anda memiliki komponen ResellerUpdate */}
      {/* <ResellerUpdate
        open={openModalEdit}
        setOpen={setOpenModalEdit}
        dataSelected={selectedData}
        refreshData={refreshData}
      /> */}

      <PopUpDeleteConfirmation
        open={openDeleteConfirmation}
        setOpen={setOpenDeleteConfirmation}
        onConfirm={handleDelete}
        loading={loadingDelete}
      />
    </>
  );
};